<template>
  <!-- Expenses as the app draws it today (Workspace369 origin/main, Oct 22 2026), at the app's own scale.
       Wide containers get the desktop shell (SidebarV2 with Finance open, the canvas header) around
       lib/view/expenses/expenses_list/expenses_list_screen.dart: the toolbar (Search Expense, Import data,
       Add Expense), the chips row (Sort By, Expense Type, Type Total, Select, the List/Grid/Table toggle)
       and the default List view grouped by day (expense_date_groups.dart, widgets/expense_card_widget.dart:
       34px type tile, name over type, amount, ⋯). Add Expense opens the desktop chooser
       (expense_create_chooser.dart), Scan shows the blocking loader with "Reading your receipt with AI…"
       (lib/services/ai/expense_scan_coordinator.dart:195, lib/view/widgets/core/loader.dart), and the
       prefilled form opens as the side panel (manage_expense_details_screen.dart: the Prefilled by AI
       banner, Details, then the collapsed sections). Below 600px it is the phone layout: Search alone, the
       shell's Add button, the day list, the chooser as a bottom sheet and the form full screen. Labels are
       app_en.arb strings, except the chooser's AI usage subtitle, which is left out (the site
       names it AI usage). Northstar Studio, its clients and the receipt are sample data. Expense types use
       the app's default receipt icon. States: `chooser`, `reading`, `review`, `saved`
       (Add Expense closes the panel, as the app does with no toast, and the new row heads Today); the server renders `saved`.
       Radii follow the site's rules where the app's are larger (loader card 18 → 10, chooser 12 → 10). -->
  <div class="xpw" :class="`xpw--${state}`" aria-hidden="true">
    <svg class="xpw-sprite" width="0" height="0">
      <symbol v-for="(d, name) in icons" :id="`xpw-${name}`" :key="name" viewBox="0 0 24 24"><path :d="d" /></symbol>
    </svg>

    <!-- Desktop and tablet -->
    <div class="xpw-desk">
      <aside class="xpw-side">
        <div class="xpw-side-top"><svg class="ic"><use href="#xpw-view_sidebar" /></svg></div>
        <div class="xpw-ws"><span class="xpw-logo">N</span><span class="xpw-ell">Northstar Studio</span><svg class="ic sm"><use href="#xpw-unfold_more" /></svg></div>
        <nav class="xpw-nav">
          <template v-for="g in nav" :key="g.k">
            <div class="xpw-ni top">
              <svg class="ic"><use :href="`#xpw-${g.i}`" /></svg><span>{{ g.k }}</span>
              <svg v-if="g.kids || g.chev" class="ic sm xpw-chev"><use :href="`#xpw-${g.open ? 'expand_more' : 'chevron_right'}`" /></svg>
            </div>
            <template v-if="g.open">
              <div v-for="c in g.kids" :key="c" class="xpw-ni child" :class="{ sel: c === 'Expenses' }"><span>{{ c }}</span></div>
            </template>
          </template>
        </nav>
        <div class="xpw-side-foot">
          <div v-for="f in foot" :key="f.k" class="xpw-ni"><svg class="ic"><use :href="`#xpw-${f.i}`" /></svg><span>{{ f.k }}</span></div>
        </div>
      </aside>

      <div class="xpw-main">
        <header class="xpw-head">
          <span class="xpw-title"><svg class="ic sm"><use href="#xpw-moneyOff" /></svg>Expenses</span>
          <span class="xpw-hsearch"><svg class="ic sm"><use href="#xpw-search" /></svg>Search...<kbd>⌘K</kbd></span>
          <span class="xpw-icbtn xpw-bell"><svg class="ic"><use href="#xpw-notifications" /></svg><b>3</b></span>
          <span class="xpw-icbtn"><svg class="ic"><use href="#xpw-settings" /></svg></span>
          <span class="xpw-icbtn"><svg class="ic"><use href="#xpw-chat_bubble_outline" /></svg></span>
        </header>

        <section class="xpw-sheet">
          <div class="xpw-page">
            <div class="xpw-bar">
              <span class="xpw-search"><svg class="ic sm"><use href="#xpw-search" /></svg>Search Expense</span>
              <span class="xpw-btn sec">Import data</span>
              <span class="xpw-btn" :class="{ pressed: state === 'chooser' }"><svg class="ic sm"><use href="#xpw-add" /></svg>Add Expense</span>
            </div>
            <div class="xpw-chips">
              <span class="xpw-chip"><svg class="ic xs"><use href="#xpw-swap_vert" /></svg>Sort By</span>
              <span class="xpw-chip"><svg class="ic xs"><use href="#xpw-category" /></svg>Expense Type</span>
              <span class="xpw-chip plain">Type Total: {{ total }}</span>
              <span class="xpw-grow" />
              <span class="xpw-select"><svg class="ic xs"><use href="#xpw-checkBox" /></svg>Select</span>
              <span class="xpw-toggle">
                <span class="on"><svg class="ic xs"><use href="#xpw-menu" /></svg>List</span>
                <span><svg class="ic xs"><use href="#xpw-gridView" /></svg>Grid</span>
                <span><svg class="ic xs"><use href="#xpw-table_chart" /></svg>Table</span>
              </span>
            </div>

            <div class="xpw-list">
              <template v-for="d in days" :key="d.label">
                <div class="xpw-day">{{ d.label }}</div>
                <div v-for="e in d.rows" :key="e.n" class="xpw-row" :class="{ fresh: e.fresh }">
                  <span class="xpw-tile"><svg class="ic sm"><use href="#xpw-receipt_long" /></svg></span>
                  <span class="xpw-rt"><b>{{ e.n }}</b><span>{{ e.t }}</span></span>
                  <b class="xpw-amt">{{ e.a }}</b>
                  <span class="xpw-dots"><svg class="ic sm"><use href="#xpw-more_vert" /></svg></span>
                </div>
              </template>
            </div>
          </div>

          <!-- Add Expense → the desktop chooser, anchored under the button -->
          <div v-if="state === 'chooser'" class="xpw-menu">
            <div class="xpw-mi on"><svg class="ic"><use href="#xpw-documentScanner" /></svg><span>Scan invoice/receipt</span></div>
            <div class="xpw-mi"><svg class="ic"><use href="#xpw-edit" /></svg><span>Enter manually</span></div>
          </div>

          <!-- Scan → the blocking loader -->
          <div v-if="state === 'reading'" class="xpw-veil">
            <div class="xpw-loader">
              <span class="xpw-spin" />
              <b>Reading your receipt with AI…</b>
            </div>
          </div>

          <!-- The prefilled form, as the side panel -->
          <aside v-if="state === 'review'" class="xpw-panel">
            <div class="xpw-ph"><svg class="ic"><use href="#xpw-close" /></svg><b>Add Expense</b></div>
            <div class="xpw-pb">
              <div class="xpw-ai"><svg class="ic sm"><use href="#xpw-auto_awesome" /></svg>{{ aiBanner }}</div>
              <div class="xpw-sec open">
                <div class="xpw-sh"><svg class="ic sm"><use href="#xpw-receipt_long" /></svg>Details<svg class="ic sm xpw-grow-r"><use href="#xpw-expand_more" /></svg></div>
                <span class="xpw-field xpw-fill">{{ scan.name }}</span>
                <span class="xpw-field xpw-fill tall">{{ scan.desc }}</span>
                <div class="xpw-pair">
                  <span class="xpw-field xpw-fill">{{ scan.amount }}</span>
                  <span class="xpw-field xpw-fill">{{ scan.date }}<svg class="ic sm"><use href="#xpw-calendar_today" /></svg></span>
                </div>
                <div class="xpw-pair type">
                  <span class="xpw-field xpw-fill">{{ scan.type }}<svg class="ic sm"><use href="#xpw-expand_more" /></svg></span>
                  <span class="xpw-sq"><svg class="ic sm"><use href="#xpw-add" /></svg></span>
                </div>
              </div>
              <div v-for="s in sections" :key="s.k" class="xpw-sec">
                <div class="xpw-sh"><svg class="ic sm"><use :href="`#xpw-${s.i}`" /></svg>{{ s.k }}<svg class="ic sm xpw-grow-r"><use href="#xpw-chevron_right" /></svg></div>
              </div>
            </div>
            <div class="xpw-pf"><span class="xpw-btn sec">Cancel</span><span class="xpw-btn">Add Expense</span></div>
          </aside>
        </section>
      </div>
    </div>

    <!-- Phone -->
    <div class="xpw-phone">
      <div class="xpp-top"><b>Expenses</b><span class="xpp-add"><svg class="ic"><use href="#xpw-add" /></svg>Add</span></div>
      <div class="xpp-body">
        <span class="xpw-search"><svg class="ic"><use href="#xpw-search" /></svg>Search Expense</span>
        <div class="xpp-chips">
          <span class="xpw-chip"><svg class="ic"><use href="#xpw-swap_vert" /></svg>Sort By</span>
          <span class="xpw-chip"><svg class="ic"><use href="#xpw-category" /></svg>Expense Type</span>
          <span class="xpw-chip plain">Type Total: {{ total }}</span>
        </div>
        <template v-for="d in days" :key="`p-${d.label}`">
          <div class="xpw-day">{{ d.label }}</div>
          <div v-for="e in d.rows" :key="`p-${e.n}`" class="xpw-row" :class="{ fresh: e.fresh }">
            <span class="xpw-tile"><svg class="ic"><use href="#xpw-receipt_long" /></svg></span>
            <span class="xpw-rt"><b>{{ e.n }}</b><span>{{ e.t }}</span></span>
            <b class="xpw-amt">{{ e.a }}</b>
            <span class="xpw-dots"><svg class="ic"><use href="#xpw-more_vert" /></svg></span>
          </div>
        </template>
      </div>
      <nav class="xpp-nav">
        <span v-for="t in tabs" :key="t.k"><svg class="ic"><use :href="`#xpw-${t.i}`" /></svg>{{ t.k }}</span>
        <span class="xpp-navmore"><svg class="ic"><use href="#xpw-add" /></svg></span>
      </nav>

      <!-- Add → the bottom sheet -->
      <div v-if="state === 'chooser'" class="xpp-scrim">
        <div class="xpp-sheet">
          <i class="xpp-handle" />
          <div class="xpp-mi on"><svg class="ic"><use href="#xpw-documentScanner" /></svg><b>Scan invoice/receipt</b></div>
          <div class="xpp-mi"><svg class="ic"><use href="#xpw-edit" /></svg><b>Enter manually</b></div>
        </div>
      </div>
      <div v-if="state === 'reading'" class="xpw-veil">
        <div class="xpw-loader"><span class="xpw-spin" /><b>Reading your receipt with AI…</b></div>
      </div>
      <!-- The prefilled form, full screen -->
      <div v-if="state === 'review'" class="xpp-form">
        <div class="xpp-fh"><svg class="ic"><use href="#xpw-arrow_back" /></svg><b>Add Expense</b></div>
        <div class="xpp-fb">
          <div class="xpw-ai"><svg class="ic"><use href="#xpw-auto_awesome" /></svg><span>{{ aiBanner }}</span></div>
          <div class="xpw-sh"><svg class="ic"><use href="#xpw-receipt_long" /></svg>Details<svg class="ic xpw-grow-r"><use href="#xpw-expand_more" /></svg></div>
          <span class="xpw-field xpw-fill">{{ scan.name }}</span>
          <span class="xpw-field xpw-fill tall">{{ scan.desc }}</span>
          <span class="xpw-field xpw-fill">{{ scan.amount }}</span>
          <span class="xpw-field xpw-fill">{{ scan.date }}<svg class="ic"><use href="#xpw-calendar_today" /></svg></span>
          <span class="xpw-field xpw-fill">{{ scan.type }}<svg class="ic"><use href="#xpw-expand_more" /></svg></span>
          <div v-for="s in sections" :key="`p-${s.k}`" class="xpw-sh shut"><svg class="ic"><use :href="`#xpw-${s.i}`" /></svg>{{ s.k }}<svg class="ic xpw-grow-r"><use href="#xpw-chevron_right" /></svg></div>
        </div>
        <div class="xpp-ff"><span class="xpw-btn sec">Cancel</span><span class="xpw-btn">Add Expense</span></div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { appIcons } from "~/data/app-icons.js";
import { expenseIcons } from "~/data/app-icons-expenses.js";

const props = defineProps({
  // chooser | reading | review | saved
  state: { type: String, default: "saved" },
});

const usedApp = ["account_balance_wallet", "add", "auto_awesome", "calendar_today", "chat_bubble_outline", "chevron_right", "dashboard", "expand_more", "forum", "handyman", "logout", "more_vert", "notifications", "people_alt", "receipt_long", "search", "settings", "support_agent", "swap_vert", "unfold_more", "view_sidebar", "work_outline", "workspaces"];
// arrow_back is the plain Material path (the app bar's back arrow).
const icons = { ...Object.fromEntries(usedApp.map((k) => [k, appIcons[k]])), ...expenseIcons, arrow_back: "M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" };

const nav = [
  { k: "Dashboard", i: "dashboard" },
  { k: "AI AutoPilot", i: "auto_awesome", chev: true },
  { k: "Clients", i: "people_alt", kids: ["Clients", "Requests"] },
  { k: "Work", i: "work_outline", kids: ["Projects", "Calendar", "Tasks", "Reminders", "Time Tracking"] },
  { k: "Finance", i: "account_balance_wallet", open: true, kids: ["Proposals", "Contracts", "Invoices", "Retainers", "Expenses", "Accounting", "Product Catalog"] },
  { k: "Communications", i: "forum", kids: ["Inbox", "Emails", "Mobile", "Campaigns", "Workflows", "Funnels"] },
  { k: "Tools", i: "handyman", kids: ["Notes", "Files", "Habits"] },
];
const foot = [
  { k: "Settings", i: "settings" },
  { k: "Support", i: "support_agent" },
  { k: "Log Out", i: "logout" },
];
const tabs = [
  { k: "Home", i: "dashboard" },
  { k: "Projects", i: "workspaces" },
  { k: "AI AutoPilot", i: "auto_awesome" },
  { k: "Invoices", i: "receipt_long" },
  { k: "Calendar", i: "calendar_today" },
];

const total = computed(() => (props.state === "saved" ? "$597.50" : "$412.90"));

const aiBanner = "✨ Prefilled by AI — review before saving.";

// What the scan read from a print shop's receipt for the Juniper Bakery menu job.
const scan = {
  name: "Menu print proofs",
  desc: "Harbor Print Co., receipt 4471: 12 menu proofs on matte stock",
  amount: "$184.60",
  date: "Oct 22, 2026",
  type: "Printing",
};
const sections = [
  { k: "Line Items", i: "formatListBulleted" },
  { k: "Linked Project", i: "folder" },
  { k: "Accounting", i: "account_balance" },
  { k: "Expense Attachments", i: "attach_file" },
];

const days = [
  { label: "Today", rows: [
    { n: "Menu print proofs", t: "Printing", a: "$184.60", fresh: true },
    { n: "Stock photo licence", t: "Software", a: "$29.00" },
    { n: "Client lunch, Marlowe Coffee Co.", t: "Meals", a: "$48.40" },
  ] },
  { label: "Yesterday", rows: [
    { n: "Courier to Juniper Bakery", t: "Shipping", a: "$32.50" },
    { n: "Domain renewal", t: "Software", a: "$24.00" },
    { n: "Printer ink", t: "Office supplies", a: "$46.90" },
  ] },
  { label: "Oct 20, 2026", rows: [
    { n: "Photo shoot props", t: "Materials", a: "$86.00" },
    { n: "Mileage to Orchard Veterinary", t: "Travel", a: "$52.10" },
    { n: "Co-working day pass", t: "Rent", a: "$35.00" },
    { n: "Font licence", t: "Software", a: "$59.00" },
  ] },
];
</script>

<style>
/* Design tokens: shadcn Neutral (the app's default scheme) as in InvoicesAppWindow. */
.xpw {
  --ink: #0a0a0a; --primary: #171717; --on-primary: #fafafa;
  --fill: #eef1f4; --canvas: #f7f7f7; --page: #fff; --edge: #ebebeb; --card-edge: #e6e6e6; --line2: #efefef;
  --ink2: rgb(10 10 10 / 0.72); --ink3: rgb(10 10 10 / 0.5); --quiet: rgb(10 10 10 / 0.45); --selected: rgb(10 10 10 / 0.06);
  --tile: rgb(23 23 23 / 0.08); --ai-f: rgb(229 229 229 / 0.45); --ai-edge: rgb(23 23 23 / 0.2);
  position: relative;
  container-type: inline-size;
  color: var(--ink);
  font-family: "Space Grotesk", ui-sans-serif, system-ui, sans-serif;
  font-size: 13px;
  font-variant-numeric: tabular-nums;
  line-height: 1.3;
  text-align: left;
}
.dark .xpw {
  --ink: #fafafa; --primary: #fafafa; --on-primary: #171717;
  --fill: #232527; --canvas: #121212; --page: #1a1a1a; --edge: #272727; --card-edge: #2c2c2c; --line2: #252525;
  --ink2: rgb(250 250 250 / 0.72); --ink3: rgb(250 250 250 / 0.5); --quiet: rgb(250 250 250 / 0.45); --selected: rgb(250 250 250 / 0.1);
  --tile: rgb(250 250 250 / 0.08); --ai-f: rgb(64 64 64 / 0.35); --ai-edge: rgb(250 250 250 / 0.2);
}
/* The window's size is reserved before its markup has streamed in (see InvoicesAppWindow). */
.xpw { min-height: 640px; }
@media (max-width: 1128px) { .xpw { min-height: 560px; } }
@media (max-width: 648px) { .xpw { min-height: 0; aspect-ratio: 390 / 700; } }
.xpw-sprite { position: absolute; width: 0; height: 0; overflow: hidden; }
.xpw .ic { display: inline-block; flex: none; width: 18px; height: 18px; fill: currentColor; vertical-align: middle; }
.xpw .ic.sm { width: 16px; height: 16px; }
.xpw .ic.xs { width: 14px; height: 14px; }
.xpw-grow { flex: 1; }
.xpw-grow-r { margin-left: auto; color: var(--ink3); }
.xpw-ell { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

/* ---------- desktop shell (as InvoicesAppWindow) ---------- */
.xpw-desk {
  display: grid;
  grid-template-columns: 252px minmax(0, 1fr);
  height: 640px;
  overflow: hidden;
  border: 1px solid var(--card-edge);
  border-radius: 10px;
  background: var(--canvas);
  box-shadow: 0 30px 60px -30px rgb(15 23 42 / 0.28), 0 12px 24px -16px rgb(15 23 42 / 0.12);
}
.dark .xpw-desk { border-color: #3a3a3a; box-shadow: inset 0 1px 0 rgb(255 255 255 / 0.06), 0 30px 60px -30px rgb(0 0 0 / 0.7); }
.dark .xpw-phone { border-color: #3a3a3a; }
.xpw-side { display: flex; flex-direction: column; min-height: 0; padding: 0 10px 10px; }
.xpw-side-top { display: flex; align-items: center; height: 48px; padding-left: 6px; color: var(--ink2); }
.xpw-ws { display: flex; align-items: center; gap: 10px; height: 40px; margin-bottom: 6px; border-bottom: 1px solid var(--edge); padding: 0 6px 6px; font-weight: 600; }
.xpw-ws .ic { margin-left: auto; color: var(--ink3); }
.xpw-logo { display: grid; flex: none; place-items: center; width: 26px; height: 26px; border-radius: 6px; background: #171717; color: #fafafa; font-size: 12px; font-weight: 700; }
.dark .xpw-logo { background: #fafafa; color: #171717; }
.xpw-nav { flex: 1; min-height: 0; overflow: hidden; }
.xpw-ni { display: flex; align-items: center; gap: 10px; height: 32px; padding: 0 8px; border-radius: 6px; color: var(--ink2); white-space: nowrap; }
.xpw-ni.top { color: var(--ink); font-weight: 600; }
.xpw-ni.child { padding-left: 38px; }
.xpw-ni.sel { background: var(--selected); color: var(--ink); font-weight: 600; }
.xpw-ni .xpw-chev { margin-left: auto; color: var(--ink3); }
.xpw-side-foot { border-top: 1px solid var(--edge); padding-top: 6px; }
.xpw-side-foot .xpw-ni { height: 30px; }
.xpw-main { display: grid; grid-template-rows: 48px minmax(0, 1fr); min-width: 0; }
.xpw-head { display: flex; align-items: center; gap: 12px; padding: 0 14px 0 4px; }
.xpw-title { display: flex; flex: 1; align-items: center; gap: 8px; font-weight: 600; }
.xpw-title .ic { color: var(--ink2); }
.xpw-hsearch { display: flex; align-items: center; gap: 8px; width: 230px; height: 30px; border: 1px solid var(--card-edge); border-radius: 6px; background: var(--page); padding: 0 6px 0 10px; color: var(--ink3); }
.xpw-hsearch kbd { margin-left: auto; border: 1px solid var(--card-edge); border-radius: 4px; padding: 0 5px; font: inherit; font-size: 11px; }
.xpw-icbtn { position: relative; display: grid; place-items: center; width: 28px; height: 28px; color: var(--ink2); }
.xpw-bell b { position: absolute; top: 0; right: 0; display: grid; place-items: center; min-width: 14px; height: 14px; border-radius: 4px; background: #e53935; color: #fff; font-size: 9px; }
.xpw-sheet { position: relative; min-height: 0; overflow: hidden; border-top: 1px solid var(--edge); border-left: 1px solid var(--edge); border-top-left-radius: 10px; background: var(--page); }

/* ---------- Expenses (expenses_list_screen.dart, desktop) ---------- */
.xpw-page { padding: 12px 14px 0; }
.xpw-bar { display: flex; align-items: center; gap: 12px; }
.xpw-search { display: flex; flex: 1; align-items: center; gap: 8px; height: 32px; border-radius: 6px; background: var(--fill); padding: 0 10px; color: var(--ink3); }
.xpw-btn { display: inline-flex; flex: none; align-items: center; gap: 6px; height: 32px; border: 1px solid transparent; border-radius: 6px; background: var(--primary); padding: 0 12px; color: var(--on-primary); font-weight: 500; white-space: nowrap; transition: box-shadow 0.2s ease; }
.xpw-btn.sec { border-color: var(--card-edge); background: var(--page); color: var(--ink); }
.xpw-btn.pressed { box-shadow: 0 0 0 3px rgb(from var(--primary) r g b / 0.18); }
.xpw-chips { display: flex; align-items: center; gap: 6px; margin-top: 12px; }
.xpw-chip { display: inline-flex; flex: none; align-items: center; gap: 5px; height: 24px; border: 1px solid var(--card-edge); border-radius: 4px; padding: 0 8px; color: var(--ink2); font-size: 12px; font-weight: 500; white-space: nowrap; }
.xpw-chip.plain { border-color: transparent; padding: 0 4px; color: var(--ink3); }
.xpw-select { display: inline-flex; flex: none; align-items: center; gap: 6px; height: 30px; margin-right: 4px; border: 1px solid rgb(from var(--ink) r g b / 0.2); border-radius: 6px; padding: 0 12px; font-weight: 500; }
.xpw-toggle { display: inline-flex; flex: none; gap: 2px; border-radius: 6px; background: var(--page); padding: 3px; box-shadow: 0 1px 4px rgb(0 0 0 / 0.1); }
.xpw-toggle span { display: inline-flex; align-items: center; gap: 5px; height: 24px; border-radius: 4px; padding: 0 10px; color: var(--ink3); font-weight: 500; }
.xpw-toggle .on { background: var(--primary); color: var(--on-primary); font-weight: 600; }
.xpw-list { margin-top: 12px; }
/* The list fades out at the window's bottom edge; the review panel sits above the fade, so its footer stays whole. */
.xpw-sheet::after { content: ""; position: absolute; z-index: 2; right: 0; bottom: 0; left: 0; height: 52px; background: linear-gradient(rgb(from var(--page) r g b / 0), var(--page)); pointer-events: none; }
.xpw-day { padding: 14px 16px 6px; color: var(--quiet); font-size: 11px; font-weight: 600; letter-spacing: 0.03em; }
.xpw-row { display: flex; align-items: center; gap: 12px; height: 58px; padding: 0 8px 0 16px; }
.xpw-row + .xpw-row { background-image: linear-gradient(var(--line2), var(--line2)); background-position: 62px 0; background-repeat: no-repeat; background-size: calc(100% - 78px) 1px; }
.xpw-tile { display: grid; flex: none; place-items: center; width: 34px; height: 34px; border-radius: 8px; background: var(--tile); color: var(--primary); }
.xpw-rt { display: grid; flex: 1; gap: 2px; min-width: 0; }
.xpw-rt b { overflow: hidden; font-size: 14px; font-weight: 500; text-overflow: ellipsis; white-space: nowrap; }
.xpw-rt span { color: var(--ink3); font-size: 12px; }
.xpw-amt { font-size: 14px; font-weight: 600; }
.xpw-dots { display: grid; place-items: center; width: 28px; height: 28px; color: var(--ink3); }

/* Chooser */
.xpw-menu { position: absolute; z-index: 4; top: 50px; right: 14px; width: 280px; border: 1px solid var(--card-edge); border-radius: 10px; background: var(--page); padding: 6px; box-shadow: 0 12px 32px rgb(0 0 0 / 0.16); animation: xpw-drop 0.25s ease-out both; }
.xpw-mi { display: flex; align-items: center; gap: 12px; height: 44px; border-radius: 6px; padding: 0 12px; font-size: 14px; font-weight: 500; }
.xpw-mi .ic { width: 20px; height: 20px; color: var(--primary); }
.xpw-mi.on { background: var(--selected); }

/* Loader (loader.dart: surface tint and a vertical blur over the page, the card centred) */
.xpw-veil { position: absolute; z-index: 4; inset: 0; display: grid; place-items: center; background: rgb(from var(--page) r g b / 0.2); -webkit-backdrop-filter: blur(2.5px); backdrop-filter: blur(2.5px); animation: xpw-fade 0.3s ease both; }
.xpw-loader { display: grid; justify-items: center; gap: 18px; width: min(322px, 80%); border: 1px solid rgb(from var(--ink) r g b / 0.08); border-radius: 10px; background: var(--page); padding: 28px 24px; box-shadow: 0 12px 28px rgb(0 0 0 / 0.08); }
.xpw-loader b { font-size: 14px; font-weight: 500; text-align: center; }
.xpw-spin { width: 36px; height: 36px; border: 3px solid var(--tile); border-top-color: var(--primary); border-radius: 50%; animation: xpw-spin 0.9s linear infinite; }

/* Side panel (45% of the width) */
.xpw-panel { position: absolute; z-index: 3; top: 0; right: 0; bottom: 0; display: flex; flex-direction: column; width: 45%; min-width: 360px; border-left: 1px solid var(--card-edge); background: var(--page); box-shadow: -16px 0 32px -20px rgb(0 0 0 / 0.25); animation: xpw-slide 0.35s cubic-bezier(0.2, 0.8, 0.2, 1) both; }
.xpw-ph { display: flex; align-items: center; gap: 14px; height: 52px; border-bottom: 1px solid var(--line2); padding: 0 16px; font-size: 16px; }
.xpw-ph b { font-weight: 600; }
.xpw-pb { display: grid; align-content: start; gap: 8px; flex: 1; min-height: 0; overflow: hidden; padding: 14px 16px; }
.xpw-ai { display: flex; align-items: center; gap: 8px; border: 1px solid var(--ai-edge); border-radius: 10px; background: var(--ai-f); padding: 10px 14px; color: var(--primary); font-size: 12px; font-weight: 500; }
.xpw-sec { border: 1px solid var(--card-edge); border-radius: 8px; padding: 0 12px; }
.xpw-sec.open { display: grid; gap: 10px; padding-bottom: 12px; }
.xpw-sh { display: flex; align-items: center; gap: 8px; height: 42px; font-weight: 600; }
.xpw-sh > .ic:first-child { color: var(--ink2); }
.xpw-field { display: flex; align-items: center; justify-content: space-between; gap: 8px; min-width: 0; height: 36px; border: 1px solid var(--card-edge); border-radius: 6px; padding: 0 10px; overflow: hidden; white-space: nowrap; }
.xpw-field.tall { height: 44px; white-space: normal; line-height: 1.25; }
.xpw-field .ic { color: var(--ink3); }
.xpw-fill { animation: xpw-fill 0.9s ease-out both; }
.xpw-pair { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
.xpw-pair.type { grid-template-columns: minmax(0, 1fr) auto; gap: 10px; }
.xpw-sq { display: grid; place-items: center; width: 36px; height: 36px; border: 1px solid var(--card-edge); border-radius: 6px; }
.xpw-pf { display: flex; justify-content: flex-end; gap: 8px; border-top: 1px solid var(--line2); padding: 10px 16px; }
@keyframes xpw-drop { from { opacity: 0; transform: translateY(-6px); } }
@keyframes xpw-fade { from { opacity: 0; } }
/* The saved expense: its row is always in the list, hidden with the rows under it lifted into its place until
   Add Expense saves it, so the rows slide down by transform and nothing reflows (no layout shift). */
.xpw-row ~ * { transition: transform 0.45s cubic-bezier(0.2, 0.8, 0.2, 1); }
.xpw:not(.xpw--saved) .xpw-row.fresh { visibility: hidden; }
.xpw:not(.xpw--saved) .xpw-row.fresh + .xpw-row { background-image: none; }
.xpw:not(.xpw--saved) .xpw-desk .xpw-row.fresh ~ * { transform: translateY(-58px); }
.xpw:not(.xpw--saved) .xpw-phone .xpw-row.fresh ~ * { transform: translateY(calc(var(--u) * -62)); }
.xpw--saved .xpw-row.fresh { animation: xpw-fill 2.4s ease-out 0.2s both; }
@keyframes xpw-spin { to { transform: rotate(360deg); } }
@keyframes xpw-slide { from { transform: translateX(24px); opacity: 0; } }
@keyframes xpw-fill { from { background: var(--selected); } to { background: transparent; } }

/* Tablet: the sidebar is collapsed, as the app's sidebar toggle leaves it. */
@container (max-width: 1080px) {
  .xpw-desk { grid-template-columns: minmax(0, 1fr); height: 560px; }
  .xpw-side, .xpw-hsearch { display: none; }
  .xpw-head { padding-left: 14px; }
  .xpw-chip.plain { display: none; }
  .xpw-panel { width: 58%; }
}
@container (max-width: 860px) {
  .xpw-toggle span:not(.on) { font-size: 0; gap: 0; }
  .xpw-select { display: none; }
}

/* ---------- phone (one app point = 1/390 of the width, text at the app's default 125%) ---------- */
.xpw-phone { display: none; }
@container (max-width: 600px) {
  .xpw-desk { display: none; }
  .xpw-phone {
    --u: calc(100cqi / 390);
    --t: calc(var(--u) * 1.25);
    position: relative;
    display: block;
    height: calc(var(--u) * 700);
    overflow: hidden;
    border: 1px solid var(--card-edge);
    border-radius: 10px;
    background: var(--page);
    font-size: calc(var(--t) * 13);
  }
  .xpw-phone .ic { width: calc(var(--t) * 15); height: calc(var(--t) * 15); }
  .xpp-top { display: flex; align-items: center; justify-content: space-between; height: calc(var(--u) * 52); padding: 0 calc(var(--u) * 16); }
  .xpp-top b { font-size: calc(var(--t) * 18); font-weight: 600; }
  .xpp-add { display: inline-flex; align-items: center; gap: calc(var(--u) * 4); height: calc(var(--u) * 35); border-radius: calc(var(--u) * 6); background: var(--primary); padding: 0 calc(var(--u) * 12); color: var(--on-primary); font-weight: 500; }
  .xpp-body { display: grid; gap: calc(var(--u) * 8); padding: 0 calc(var(--u) * 10); }
  .xpw-phone .xpw-search { height: calc(var(--u) * 44); border-radius: calc(var(--u) * 6); font-size: calc(var(--t) * 13); }
  .xpp-chips { display: flex; gap: calc(var(--u) * 6); overflow: hidden; }
  .xpw-phone .xpw-chip { height: calc(var(--u) * 36); gap: calc(var(--u) * 4); padding: 0 calc(var(--u) * 10); font-size: calc(var(--t) * 12); }
  .xpw-phone .xpw-day { padding: calc(var(--u) * 10) calc(var(--u) * 16) calc(var(--u) * 4); font-size: calc(var(--t) * 11); }
  .xpw-phone .xpw-row { gap: calc(var(--u) * 12); height: calc(var(--u) * 62); padding: calc(var(--u) * 8) calc(var(--u) * 4) calc(var(--u) * 8) calc(var(--u) * 16); background-position: calc(var(--u) * 62) 0; background-size: calc(100% - var(--u) * 78) 1px; }
  .xpw-phone .xpw-tile { width: calc(var(--u) * 34); height: calc(var(--u) * 34); }
  .xpw-phone .xpw-rt b, .xpw-phone .xpw-amt { font-size: calc(var(--t) * 14); }
  .xpw-phone .xpw-rt span { font-size: calc(var(--t) * 12); }
  .xpp-nav { position: absolute; z-index: 3; right: calc(var(--u) * 12); bottom: calc(var(--u) * 14); left: calc(var(--u) * 12); display: flex; align-items: center; gap: calc(var(--u) * 4); border: 1px solid var(--card-edge); border-radius: 10px; background: var(--page); padding: calc(var(--u) * 6); box-shadow: 0 8px 24px rgb(0 0 0 / 0.12), 0 -14px 18px -6px var(--page); }
  .xpp-nav::after { content: ""; position: absolute; right: calc(var(--u) * -13); bottom: calc(var(--u) * -15); left: calc(var(--u) * -13); height: calc(var(--u) * 14); background: var(--page); }
  .xpp-nav > span:not(.xpp-navmore) { display: grid; flex: 1; justify-items: center; gap: calc(var(--u) * 2); color: var(--ink3); font-size: calc(var(--u) * 10.5); white-space: nowrap; }
  .xpp-nav .ic { width: calc(var(--u) * 22); height: calc(var(--u) * 22); }
  .xpp-navmore { display: grid; flex: none; place-items: center; width: calc(var(--u) * 44); height: calc(var(--u) * 44); border-radius: 8px; background: var(--primary); color: var(--on-primary); }
  .xpp-scrim { position: absolute; z-index: 5; inset: 0; display: flex; align-items: flex-end; background: rgb(0 0 0 / 0.32); animation: xpw-fade 0.25s ease both; }
  .xpp-sheet { width: 100%; border-radius: 10px 10px 0 0; background: var(--page); padding: calc(var(--u) * 10) 0 calc(var(--u) * 28); animation: xpp-up 0.3s ease-out both; }
  .xpp-handle { display: block; width: calc(var(--u) * 40); height: calc(var(--u) * 4); margin: 0 auto calc(var(--u) * 10); border-radius: 2px; background: var(--quiet); }
  .xpp-mi { display: flex; align-items: center; gap: calc(var(--u) * 16); height: calc(var(--u) * 56); padding: 0 calc(var(--u) * 20); font-size: calc(var(--t) * 14); }
  .xpp-mi b { font-weight: 500; }
  .xpp-mi .ic { width: calc(var(--u) * 22); height: calc(var(--u) * 22); color: var(--primary); }
  .xpw-phone .xpw-veil { z-index: 5; }
  .xpw-phone .xpw-loader { gap: calc(var(--u) * 16); padding: calc(var(--u) * 24) calc(var(--u) * 20); }
  .xpw-phone .xpw-loader b { font-size: calc(var(--t) * 13); }
  .xpp-form { position: absolute; z-index: 5; inset: 0; display: flex; flex-direction: column; background: var(--page); animation: xpp-up 0.35s cubic-bezier(0.2, 0.8, 0.2, 1) both; }
  .xpp-fh { display: flex; align-items: center; gap: calc(var(--u) * 16); height: calc(var(--u) * 52); padding: 0 calc(var(--u) * 16); font-size: calc(var(--t) * 16); }
  .xpp-fh b { font-weight: 600; }
  .xpp-fb { display: grid; align-content: start; gap: calc(var(--u) * 10); flex: 1; min-height: 0; overflow: hidden; padding: calc(var(--u) * 6) calc(var(--u) * 16); }
  .xpw-phone .xpw-ai { gap: calc(var(--u) * 8); padding: calc(var(--u) * 10) calc(var(--u) * 12); font-size: calc(var(--t) * 11); }
  .xpw-phone .xpw-sh { height: calc(var(--u) * 40); gap: calc(var(--u) * 8); }
  .xpw-phone .xpw-sh.shut { border-top: 1px solid var(--line2); }
  .xpw-phone .xpw-field { height: calc(var(--u) * 44); border-radius: calc(var(--u) * 6); padding: 0 calc(var(--u) * 12); }
  .xpw-phone .xpw-field.tall { height: calc(var(--u) * 64); padding-block: calc(var(--u) * 8); }
  .xpp-ff { display: grid; grid-template-columns: 1fr 1fr; gap: calc(var(--u) * 10); border-top: 1px solid var(--line2); padding: calc(var(--u) * 10) calc(var(--u) * 16) calc(var(--u) * 18); }
  .xpp-ff .xpw-btn { justify-content: center; height: calc(var(--u) * 44); }
  @keyframes xpp-up { from { transform: translateY(16%); opacity: 0; } }
}
@media (prefers-reduced-motion: reduce) { .xpw * { animation: none !important; transition: none !important; } }
</style>
